import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import BusinessInfo from './BusinessInfo';
import BusinessDescription from './BusinessDescription';
import VoiceSelection from './VoiceSelection';
import PhoneNumberSelector from './PhoneNumberSelector';
import BehaviorSettings from './BehaviorSettings';

import configService from '../services/configService';
import './VoiceSelection.css';

// Dynamic step configuration
const SETUP_STEPS = [
  {
    id: 1,
    title: 'Business Information',
    subtitle: 'Website & Phone Number',
    component: BusinessInfo,
    editButtonText: 'Edit Info'
  },
  {
    id: 2,
    title: 'Business Description',
    subtitle: 'AI-generated description & assistant settings',
    component: BusinessDescription,
    editButtonText: 'Edit Description'
  },
  {
    id: 3,
    title: 'Customize Assistant',
    subtitle: 'Voice & personality settings',
    component: VoiceSelection,
    editButtonText: 'Change Voice'
  },
  {
    id: 4,
    title: 'Choose Phone Number',
    subtitle: 'Select your assistant\'s number',
    component: PhoneNumberSelector,
    editButtonText: 'Change Number'
  },
  {
    id: 5,
    title: 'Administrator Contact',
    subtitle: 'Configure Administrator Account',
    component: BehaviorSettings,
    editButtonText: 'Edit'
  }
];

const TOTAL_STEPS = SETUP_STEPS.length;

function Setup() {
  const { user, logout, updateUserProfile } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isSetupCompleted, setIsSetupCompleted] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  const businessName = user?.attributes?.['custom:business_name'] || 'Your Business';

  // Expose unsaved changes tracking to child components
  React.useEffect(() => {
    window.setHasUnsavedChanges = setHasUnsavedChanges;
    return () => {
      delete window.setHasUnsavedChanges;
    };
  }, []);

  // Dynamic contextual save functions for each step
  const stepSaveFunctions = {
    1: async () => {
      console.log('💾 Saving Step 1: Business Info');
      if (window.saveBusinessInfo) {
        return await window.saveBusinessInfo();
      }
      console.log('⚠️ No BusinessInfo save function found');
      return false;
    },
    2: async () => {
      console.log('💾 Saving Step 2: Business Description');
      try {
        if (window.saveBusinessDescription) {
          return await window.saveBusinessDescription();
        }
        console.log('⚠️ No BusinessDescription save function found');
        return false;
      } catch (error) {
        console.error('❌ Error saving business description:', error);
        return false;
      }
    },
    3: async () => {
      console.log('💾 Saving Step 3: Voice & Personality');
      try {
        return true;
      } catch (error) {
        console.error('Voice save error:', error);
        return false;
      }
    },
    4: async () => {
      console.log('💾 Saving Step 4: Phone Number');
      try {
        const cognitoUserId = user?.attributes?.sub;
        if (cognitoUserId) {
          const config = await configService.getBusinessConfigByUserId(cognitoUserId);
          if (config?.assistant_phone) {
            console.log('📞 Phone already configured:', config.assistant_phone);
            return true;
          }
          
          // Check if there's a phone number to save (from PhoneNumberSelector state)
          if (window.selectedAssistantPhone) {
            console.log('📞 Saving selected phone:', window.selectedAssistantPhone);
            await configService.saveAssistantPhone(window.selectedAssistantPhone);
            return true;
          }
        }
        console.log('⚠️ No phone selected yet');
        return false;
      } catch (error) {
        console.error('Phone check error:', error);
        return false;
      }
    },
    5: async () => {
      console.log('💾 STEP 5 SAVE: Starting Step 5 save function (final step)');
      try {
        if (window.behaviorSettingsSave) {
          const saveResult = await window.behaviorSettingsSave();
          if (!saveResult) {
            console.log('💾 STEP 5 SAVE: Behavior settings save failed');
            return false;
          }
          console.log('💾 STEP 5 SAVE: Behavior settings saved successfully');
        } else {
          console.log('⚠️ No behavior settings save function found');
          return false;
        }
        
        setIsSetupCompleted(true);
        console.log('💾 STEP 5 SAVE: Setup marked as completed');
        
        return true;
      } catch (error) {
        console.error('💾 STEP 5 SAVE: Error in final step:', error);
        return false;
      }
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const planId = urlParams.get('plan');
    
    if (paymentStatus === 'success' && planId) {
      console.log('🎉 Payment successful for plan:', planId);
      window.history.replaceState({}, document.title, window.location.pathname);
      showToast('Payment successful! Your subscription is now active.');
    }
    
    loadConfigurationStatus();
  }, [user]);

  const loadConfigurationStatus = async () => {
    console.log('🔄 LOAD CONFIG: Starting loadConfigurationStatus');
    
    try {
      // Check if user is confirmed first
      if (!user) {
        navigate('/login');
        return;
      }
      
      // Check email verification status from JWT (skip for OAuth users)
      const isOAuthUser = user?.username?.includes('Google_') || user?.username?.includes('Facebook_') || user?.username?.includes('LoginWithAmazon_') || user?.username?.includes('SignInWithApple_');
      const emailVerified = user?.attributes?.email_verified;
      
      if (!isOAuthUser && (emailVerified === false || emailVerified === 'false')) {
        console.log('Email/password user not verified, redirecting to login');
        navigate('/login');
        return;
      } else if (isOAuthUser) {
        console.log('OAuth user detected, skipping email verification check');
      }
      
      const cognitoUserId = configService.getUserId();
      const businessId = configService.getBusinessId();
      
      if (!cognitoUserId) {
        console.log('🔄 LOAD CONFIG: No user ID from JWT, setting loading false');
        setLoading(false);
        return;
      }
      
      console.log('🔄 LOAD CONFIG: User ID:', cognitoUserId);
      console.log('🔄 LOAD CONFIG: Business ID:', businessId);
      
      let config = null;
      try {
        console.log('🔄 LOAD CONFIG: Loading config by user ID...');
        config = await configService.getBusinessConfigByUserId(cognitoUserId);
        console.log('🔄 LOAD CONFIG: Config loaded:', config);
      } catch (configError) {
        console.log('🔄 LOAD CONFIG: No config found, starting at step 1:', configError);
        // If it's a 404 error, user needs to complete setup
        if (configError.message.includes('404') || configError.message.includes('Configuration not found')) {
          console.log('🔄 LOAD CONFIG: User has no configuration, starting setup from step 1');
        }
        setCurrentStep(1);
        setLoading(false);
        return;
      }
      
      let currentStep = 1;
      let setupCompleted = false;
      console.log('🔄 LOAD CONFIG: Starting step determination from step 1');
      
      if (config) {
        const step1Complete = !!(config.business_name && config.phone_number && config.website && config.address);
        console.log('🔄 LOAD CONFIG: Step 1 complete?', step1Complete, {
          business_name: !!config.business_name,
          phone_number: !!config.phone_number,
          website: !!config.website,
          address: !!config.address
        });
        
        if (step1Complete) {
          currentStep = 2;
          
          const step2Complete = !!(config.business_description && config.assistant_name);
          console.log('🔄 LOAD CONFIG: Step 2 complete?', step2Complete, {
            business_description: !!config.business_description,
            assistant_name: !!config.assistant_name
          });
          
          if (step2Complete) {
            currentStep = 3;
            
            const step3Complete = !!(config.voice_settings && config.formality && config.verbosity && config.empathy);
            console.log('🔄 LOAD CONFIG: Step 3 complete?', step3Complete, {
              voice_settings: !!config.voice_settings,
              formality: !!config.formality,
              verbosity: !!config.verbosity,
              empathy: !!config.empathy
            });
            
            if (step3Complete) {
              currentStep = 4;
              
              const step4Complete = !!config.assistant_phone;
              console.log('🔄 LOAD CONFIG: Step 4 complete?', step4Complete, {
                assistant_phone: !!config.assistant_phone
              });
              
              if (step4Complete) {
                currentStep = 5;
                
                const step5Complete = !!(config.manager_number && config.escalation_email);
                console.log('🔄 LOAD CONFIG: Step 5 complete?', step5Complete, {
                  manager_number: !!config.manager_number,
                  escalation_email: !!config.escalation_email
                });
                
                // Check if all steps are complete (setup finished)
                setupCompleted = step1Complete && step2Complete && step3Complete && step4Complete && step5Complete;
                console.log('🔄 LOAD CONFIG: Setup completed?', setupCompleted, 'All steps:', {
                  step1: step1Complete,
                  step2: step2Complete,
                  step3: step3Complete,
                  step4: step4Complete,
                  step5: step5Complete
                });
                
                // If setup is completed, redirect to dashboard
                // Backend already updated JWT when behavior settings were saved
                if (setupCompleted) {
                  console.log('🔄 LOAD CONFIG: Setup is complete, redirecting to dashboard');
                  navigate('/dashboard');
                  return;
                }
              }
            }
          }
        }
      }
      
      console.log('🔄 LOAD CONFIG: Final determined step:', currentStep);
      setCurrentStep(currentStep);
      setIsSetupCompleted(setupCompleted);
    } catch (error) {
      console.warn('🔄 LOAD CONFIG: Error, starting at step 1:', error);
      setCurrentStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = async () => {
    console.log('🎯 SETUP COMPLETE: Function called');
    
    try {
      setLoading(true);
      
      if (!user?.attributes?.sub) {
        console.error('🎯 SETUP COMPLETE: No user ID found');
        throw new Error('User not authenticated');
      }
      
      navigate('/dashboard');
      return true;
    } catch (error) {
      console.error('🎯 SETUP COMPLETE: Error in handleSetupComplete:', error);
      showToast(`Failed to complete setup: ${error.message}`, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleStepNavigation = (targetStep) => {
    if (!isSetupCompleted && targetStep > currentStep) {
      // In initial setup mode, can't skip ahead
      return;
    }
    
    if (hasUnsavedChanges) {
      // Show modal to handle unsaved changes
      setPendingNavigation(targetStep);
      setShowUnsavedModal(true);
    } else {
      // Navigate directly
      setCurrentStep(targetStep);
    }
  };

  const handleUnsavedModalSave = async () => {
    try {
      const saveFunction = stepSaveFunctions[currentStep];
      if (saveFunction) {
        const saveSuccess = await saveFunction();
        if (!saveSuccess) {
          showToast('Failed to save changes. Please try again.', 'error');
          return;
        }
      }
      setHasUnsavedChanges(false);
      setShowUnsavedModal(false);
      if (pendingNavigation !== null) {
        setCurrentStep(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      showToast('Error saving changes: ' + error.message, 'error');
    }
  };

  const handleUnsavedModalDiscard = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    if (pendingNavigation !== null) {
      setCurrentStep(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const getStepStatus = (step) => {
    if (step < currentStep) return 'completed';
    if (step === currentStep) return 'current';
    return 'pending';
  };

  const getStepStyles = (step) => {
    const status = getStepStatus(step);
    if (status === 'completed') {
      return {
        background: '#f0fdf4',
        border: '2px solid #10b981',
        numberBg: '#10b981',
        numberColor: 'white',
        icon: '✓'
      };
    }
    if (status === 'current') {
      return {
        background: '#eff6ff',
        border: '2px solid #2563eb',
        numberBg: '#2563eb',
        numberColor: 'white',
        icon: step
      };
    }
    return {
      background: 'white',
      border: '2px solid #e5e7eb',
      numberBg: '#e5e7eb',
      numberColor: '#64748b',
      icon: step
    };
  };

  if (loading) {
    return (
      <>
        <nav className="flex justify-between items-center px-6 py-4 bg-white shadow-sm fixed top-0 w-full z-50">
          <div className="text-xl font-bold text-blue-500">
            <Link to="/" className="text-blue-500 no-underline">Remote Assistant</Link>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={logout} className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer">
              Sign Out
            </button>
          </div>
        </nav>
        <div className="pt-24 pb-8 px-8 max-w-4xl mx-auto text-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-8"></div>
          <h2 className="text-2xl font-semibold text-gray-900">Loading your setup...</h2>
        </div>
      </>
    );
  }

  return (
    <>
      <nav className="flex justify-between items-center px-6 py-4 bg-white shadow-sm fixed top-0 w-full z-50">
        <div className="text-xl font-bold text-blue-500">
          <Link to="/" className="text-blue-500 no-underline">Remote Assistant</Link>
        </div>
        <div className="flex items-center gap-6">
          <button onClick={logout} className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer">
            Sign Out
          </button>
        </div>
      </nav>

      <div className="pt-24">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Setup Your Assistant
          </h1>
          <p className="text-lg text-gray-600">Let's configure {businessName} for success</p>
        </div>

        <div className="flex justify-center items-center min-h-96 mb-8 px-8">
          {(() => {
            const currentStepData = SETUP_STEPS.find(step => step.id === currentStep);
            const StepComponent = currentStepData.component;
            const stepStyles = getStepStyles(currentStep);
            
            return (
              <div className="bg-white p-8 rounded-2xl shadow-sm border max-w-2xl w-full text-center" style={{
                background: stepStyles.background,
                border: stepStyles.border
              }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold mb-6 mx-auto" style={{
                  background: stepStyles.numberBg,
                  color: stepStyles.numberColor
                }}>
                  {stepStyles.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{currentStepData.title}</h3>
                <p className="text-gray-600 mb-6">{currentStepData.subtitle}</p>
                {currentStep === 3 ? (
                  <VoiceSelection 
                    phoneNumber={user?.attributes?.phone_number} 
                    onVoiceSelected={() => {}} 
                    currentVoiceSettings={user?.attributes?.['custom:voice_settings']} 
                  />
                ) : currentStep === 5 ? (
                  <BehaviorSettings onComplete={() => {}} />
                ) : (
                  <StepComponent onComplete={() => {}} />
                )}
              </div>
            );
          })()}
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center items-center gap-4 mb-8 px-8">
          {SETUP_STEPS.map(step => {
            const canNavigate = isSetupCompleted || step.id <= currentStep;
            return (
              <button 
                key={step.id} 
                onClick={() => canNavigate && handleStepNavigation(step.id)} 
                className={`w-3 h-3 rounded-full border-none transition-all duration-300 ${
                  canNavigate
                    ? 'bg-blue-500 cursor-pointer' 
                    : 'bg-gray-200 cursor-not-allowed opacity-50'
                }`}
                title={
                  isSetupCompleted 
                    ? `Step ${step.id}: ${step.title}` 
                    : (step.id <= currentStep ? `Step ${step.id}` : `Complete previous steps first`)
                }
                disabled={!canNavigate}
              />
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center px-8 mb-16">
          <button 
            onClick={() => handleStepNavigation(Math.max(1, currentStep - 1))} 
            disabled={currentStep === 1} 
            className={`px-6 py-3 border-none rounded-xl font-medium transition-colors ${
              currentStep === 1 
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                : 'bg-gray-500 hover:bg-gray-600 text-white cursor-pointer'
            }`}
          >
            ←
          </button>
          
          <span className="text-gray-600 font-medium">
            Step {currentStep} of {TOTAL_STEPS} {isSetupCompleted && '(Editing)'}
          </span>
          
          <button
            onClick={async () => {
              console.log(`🔄 SAVE BUTTON: Starting save for step ${currentStep}`);
              
              try {
                const saveFunction = stepSaveFunctions[currentStep];
                console.log(`🔄 SAVE BUTTON: Save function exists for step ${currentStep}:`, !!saveFunction);
                
                if (saveFunction) {
                  console.log(`🔄 SAVE BUTTON: Executing save function for step ${currentStep}`);
                  const saveSuccess = await saveFunction();
                  console.log(`🔄 SAVE BUTTON: Save function result for step ${currentStep}:`, saveSuccess);
                  
                  if (!saveSuccess) {
                    console.error(`❌ SAVE BUTTON: Save failed for step ${currentStep}`);
                    showToast(`Failed to save step ${currentStep}. Please check the console for details.`, 'error');
                    return;
                  }
                  
                  console.log(`✅ SAVE BUTTON: Save successful for step ${currentStep}`);
                  setHasUnsavedChanges(false);
                } else {
                  console.log(`⚠️ SAVE BUTTON: No save function defined for step ${currentStep}`);
                }
                
                console.log(`🔄 SAVE BUTTON: Proceeding to navigation logic`);
                if (currentStep === TOTAL_STEPS && !isSetupCompleted) {
                  console.log(`🔄 SAVE BUTTON: Last step, setup completed, navigating to dashboard`);
                  // Setup completion was already handled in the save function
                  navigate('/dashboard');
                } else if (currentStep < TOTAL_STEPS) {
                  console.log(`🔄 SAVE BUTTON: Advancing to step ${currentStep + 1}`);
                  setCurrentStep(currentStep + 1);
                } else if (isSetupCompleted) {
                  console.log(`🔄 SAVE BUTTON: Setup completed, navigating to dashboard`);
                  navigate('/dashboard');
                }
              } catch (error) {
                console.error(`❌ SAVE BUTTON: Exception in save for step ${currentStep}:`, error);
                showToast(`Error saving step ${currentStep}: ${error.message}`, 'error');
              }
            }}
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white border-none rounded-xl cursor-pointer font-medium transition-colors"
          >
            {isSetupCompleted 
              ? 'Save Changes' 
              : (currentStep === TOTAL_STEPS ? 'Complete Setup' : 'Save & Continue →')
            }
          </button>
        </div>

        {/* Unsaved Changes Modal */}
        {showUnsavedModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Unsaved Changes
              </h3>
              <p className="text-gray-600 mb-6">
                You have unsaved changes on this step. What would you like to do?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleUnsavedModalDiscard}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Discard Changes
                </button>
                <button
                  onClick={handleUnsavedModalSave}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Save & Continue
                </button>
              </div>
            </div>
          </div>
        )}
        
      </div>
    </>
  );
}

export default Setup;